import { MagpieConnection } from 'magpie-lib/connection';
import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useStore } from 'react-redux';
import { navigationPublicApiTopicDef } from 'navigation-messaging/topics';
import { bookmarksApi } from 'navigation-messaging/messages';
import { Metrics } from 'foundations-theming/utils/metrics';
import { PUBLIC_API_METHODS, PUBLIC_API_USED } from '../../../constants/metrics';
import { addBookmark, removeBookmark } from '../../../thunks/bookmarks/bookmarks-api';
import { navPersonaService } from '../../../services/nav-persona';
import { selectNavItemsByPath, selectMatchingNavItemPath } from '../../../selectors';
const filter = ({
  senderFrameId
}, {
  frameId
}) => senderFrameId === frameId;

/**
 * Hook for handling bookmarks retrieval and management via Public API
 */
export const useBookmarksPublicApi = () => {
  const dispatch = useAppDispatch();
  const store = useStore();
  const navItemsByPath = useAppSelector(selectNavItemsByPath);

  // Helper function to find matching nav item for a given path using the selector
  const getNavItemForPath = useCallback(path => {
    const state = store.getState();
    const activePath = selectMatchingNavItemPath(state, {
      pathname: path
    });
    return navItemsByPath[activePath !== null && activePath !== void 0 ? activePath : ''];
  }, [store, navItemsByPath]);

  /**
   * Handler for the GET_CURRENT_BOOKMARKS public API method
   */
  const onGetCurrentBookmarks = useCallback(async (_, {
    reply
  }) => {
    Metrics.counter(PUBLIC_API_USED, {
      action: PUBLIC_API_METHODS.GET_CURRENT_BOOKMARKS
    }).increment();
    const bookmarks = await navPersonaService.getBookmarks();
    reply(bookmarksApi.currentBookmarks.create(bookmarks));
  }, []);

  /**
   * Handler for the ADD_BOOKMARK public API method
   */
  const onAddBookmark = useCallback(async ({
    payload: {
      path
    }
  }, {
    reply
  }) => {
    Metrics.counter(PUBLIC_API_USED, {
      action: PUBLIC_API_METHODS.ADD_BOOKMARK
    }).increment();
    const navItem = getNavItemForPath(path);
    if (!navItem) {
      reply(bookmarksApi.bookmarkChanged.create({
        success: false,
        bookmark: {
          path
        }
      }));
      return;
    }
    const result = await dispatch(addBookmark({
      navItemId: navItem.id
    }));
    const success = addBookmark.fulfilled.match(result);
    reply(bookmarksApi.bookmarkChanged.create({
      success,
      bookmark: {
        path
      }
    }));
  }, [dispatch, getNavItemForPath]);

  /**
   * Handler for the REMOVE_BOOKMARK public API method
   */
  const onRemoveBookmark = useCallback(async ({
    payload: {
      path
    }
  }, {
    reply
  }) => {
    Metrics.counter(PUBLIC_API_USED, {
      action: PUBLIC_API_METHODS.REMOVE_BOOKMARK
    }).increment();
    const navItem = getNavItemForPath(path);
    if (!navItem) {
      reply(bookmarksApi.bookmarkChanged.create({
        success: false,
        bookmark: {
          path
        }
      }));
      return;
    }
    const result = await dispatch(removeBookmark({
      navItemId: navItem.id
    }));
    const success = removeBookmark.fulfilled.match(result);
    reply(bookmarksApi.bookmarkChanged.create({
      success,
      bookmark: {
        path
      }
    }));
  }, [dispatch, getNavItemForPath]);
  useEffect(() => {
    const topic = MagpieConnection.get().useTopic(navigationPublicApiTopicDef);
    const unsubGetBookmarks = topic.on(bookmarksApi.getCurrentBookmarks.getType(), onGetCurrentBookmarks, {
      filter
    });
    const unsubAddBookmark = topic.on(bookmarksApi.addBookmark.getType(), onAddBookmark, {
      filter
    });
    const unsubRemoveBookmark = topic.on(bookmarksApi.removeBookmark.getType(), onRemoveBookmark, {
      filter
    });
    return () => {
      unsubGetBookmarks();
      unsubAddBookmark();
      unsubRemoveBookmark();
    };
  }, [onGetCurrentBookmarks, onAddBookmark, onRemoveBookmark]);
};